import React, { useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { getUserData, updatePlayerProfile } from "../../features/slices/UserSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  getMaxDateOfBirth,
  isAtLeastMinAge,
  MIN_REGISTRATION_AGE,
} from "../../utils/registrationConfig";

/** Fields this form owns — anything else on userData (balances, kyc…) is never posted back. */
const PROFILE_FIELDS = [
  "username",
  "firstName",
  "lastName",
  "dob",
  "country",
  "state",
  "city",
  "address",
  "pincode",
];

const LOCKABLE_FIELDS = ["username", "firstName", "lastName", "dob"];

const REQUIRED_FIELDS = {
  username: "Username",
  firstName: "First Name",
  lastName: "Last Name",
  dob: "Birthdate",
  country: "Country",
};

const OPTIONAL_FIELDS = {
  state: "State",
  city: "City",
  address: "Address",
  pincode: "Pin Code",
};

// Same character set the signup API enforces; the length is PUT /player/me's.
const USERNAME_PATTERN = /^[a-zA-Z0-9_.-]+$/;
// Letters from any script, plus the separators real names and places use.
const NAME_PATTERN = /^\p{L}[\p{L}\p{M} .'-]*$/u;
const PINCODE_PATTERN = /^[a-zA-Z0-9 -]+$/;

const getValue = (source, field) => (source?.[field] ?? "").toString();

const getFieldError = (field, rawValue) => {
  const value = rawValue.trim();
  const label = REQUIRED_FIELDS[field] || OPTIONAL_FIELDS[field];

  if (!value) return REQUIRED_FIELDS[field] ? `Please enter ${label}` : "";

  if (field === "username") {
    if (value.length < 3 || value.length > 30) {
      return "Username must be 3 to 30 characters";
    }
    return USERNAME_PATTERN.test(value)
      ? ""
      : "Username can only use letters, numbers, dots, dashes and underscores";
  }
  if (field === "dob") {
    return isAtLeastMinAge(value)
      ? ""
      : `You must be at least ${MIN_REGISTRATION_AGE} years old`;
  }
  // The maximum lengths for address and pin code are PUT /player/me's.
  if (field === "address") {
    return value.length < 5 || value.length > 255
      ? "Address must be 5 to 255 characters"
      : "";
  }
  if (field === "pincode") {
    if (value.length < 3 || value.length > 12) {
      return "Pin Code must be 3 to 12 characters";
    }
    return PINCODE_PATTERN.test(value)
      ? ""
      : "Pin Code can only use letters, numbers, spaces and dashes";
  }
  if (value.length < 2 || value.length > 100) {
    return `${label} must be 2 to 100 characters`;
  }
  return NAME_PATTERN.test(value) ? "" : `${label} can only contain letters`;
};

const getErrorText = (error, fallback = "Could not update your profile") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  return error.error || error.detail || error.message || fallback;
};

export default function UserProfile() {
  const user = useSelector((state) => state.user);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [userInfo, setUserInfo] = useState({});
  // While the form is dirty the store must not overwrite what is being typed:
  // socket balance updates change userData on every tick.
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // Errors stay hidden until a field is left or the form is submitted, so
  // nobody is told off while still typing.
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const dispatch = useDispatch();

  const changedFields = useMemo(
    () =>
      PROFILE_FIELDS.reduce((changes, field) => {
        const value = getValue(userInfo, field).trim();
        if (value !== getValue(user.userData, field)) changes[field] = value;
        return changes;
      }, {}),
    [userInfo, user.userData]
  );
  const hasChanges = Object.keys(changedFields).length > 0;

  // Identity fields and the birthdate are set once and cannot be changed
  // afterwards — they render greyed out and unfocusable so it reads as locked
  // before anyone clicks it.
  const isLocked = (field) =>
    LOCKABLE_FIELDS.includes(field) && Boolean(getValue(user.userData, field));

  const lockIcon = (field) =>
    isLocked(field) ? (
      <i
        className="bi bi-lock-fill mx-1 locked-field-icon"
        title="This cannot be changed"
      ></i>
    ) : null;

  // Locked values are not the player's to fix, so they never block a save.
  const fieldErrors = PROFILE_FIELDS.reduce((errors, field) => {
    const error = isLocked(field)
      ? ""
      : getFieldError(field, getValue(userInfo, field));
    if (error) errors[field] = error;
    return errors;
  }, {});

  const visibleError = (field) =>
    submitted || touched[field] ? fieldErrors[field] : "";

  const touchField = (field) => () =>
    setTouched((fields) => ({ ...fields, [field]: true }));

  const inputClass = (field) =>
    `form-control bg-black border border-light border-opacity-50 ${
      visibleError(field) ? "field-invalid" : ""
    }`;

  const fieldError = (field) =>
    visibleError(field) ? (
      <p className="field-error" role="alert">
        {visibleError(field)}
      </p>
    ) : null;

  const setField = (field) => (event) => {
    const { value } = event.target;
    setMessage(null);
    setIsDirty(true);
    setUserInfo((userdetail) => ({ ...userdetail, [field]: value }));
  };

  const updateUserInfo = (event) => {
    event.preventDefault();
    if (saving) return;

    setSubmitted(true);
    if (Object.keys(fieldErrors).length > 0) {
      setMessage(null);
      return;
    }
    if (!hasChanges) {
      setMessage({ type: "error", text: "No changes to update" });
      return;
    }

    setMessage(null);
    setSaving(true);
    dispatch(updatePlayerProfile(changedFields))
      .unwrap()
      // Stay dirty until the refetch lands so the form never flashes old values.
      .then(() =>
        dispatch(getUserData({ source: "profile_update", showLoader: false }))
      )
      .then(() => {
        setIsDirty(false);
        setTouched({});
        setSubmitted(false);
        setMessage({ type: "success", text: "Profile updated successfully!" });
      })
      .catch((error) => {
        setMessage({ type: "error", text: getErrorText(error) });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    if (isDirty) return;
    setUserInfo(user.userData || {});
  }, [user.userData, isDirty]);

  useEffect(() => {
    if (message?.type !== "success") return undefined;
    const timer = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  const updateButton = (
    <button
      className={`standard-btn update-profile ${isMobile ? "w-100" : ""}`}
      type="submit"
      onClick={updateUserInfo}
      disabled={saving || !hasChanges}
    >
      {saving ? "Updating..." : "Update"}
    </button>
  );

  return (
    <div className="user-profile">
      <form
        className="needs-validation user-info-form"
        onSubmit={updateUserInfo}
        noValidate
      >
        <div className="profile-field--full d-flex align-items-center">
          <h6 className="w-100 mb-0">Profile</h6>
          {!isMobile && updateButton}
        </div>
        {message && (
          <div className="profile-field--full">
            <p
              className="text--medium mb-0"
              style={{ color: message.type === "success" ? "#32d172" : "#E42D2D" }}
            >
              <i
                className={`bi ${
                  message.type === "success"
                    ? "bi-check-circle"
                    : "bi-exclamation-circle"
                }`}
              ></i>{" "}
              {message.text}
            </p>
          </div>
        )}
        <div className="profile-field profile-field--6">
          <label
            for="validationCustom01"
            className="form-label text--medium text--light"
          >
            Username
            {!isLocked("username") && (
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            )}
            {lockIcon("username")}
          </label>
          <input
            type="text"
            className={inputClass("username")}
            id="validationCustom01"
            placeholder="Username"
            value={userInfo?.username || ""}
            onChange={setField("username")}
            onBlur={touchField("username")}
            disabled={isLocked("username")}
            required
            autoComplete="off"
          />
          {fieldError("username")}
        </div>
        <div className="profile-field profile-field--3 profile-field--sm-half">
          <label
            for="validationCustom02"
            className="form-label text--medium text--light"
          >
            First Name
            {!isLocked("firstName") && (
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            )}
            {lockIcon("firstName")}
          </label>
          <input
            type="text"
            className={inputClass("firstName")}
            id="validationCustom02"
            placeholder="First Name"
            required
            value={userInfo?.firstName || ""}
            onChange={setField("firstName")}
            onBlur={touchField("firstName")}
            disabled={isLocked("firstName")}
            autoComplete="off"
          />
          {fieldError("firstName")}
        </div>
        <div className="profile-field profile-field--3 profile-field--sm-half">
          <label
            for="validationCustomUsername"
            className="form-label text--medium text--light"
          >
            Last name
            {!isLocked("lastName") && (
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            )}
            {lockIcon("lastName")}
          </label>
          <input
            type="text"
            className={inputClass("lastName")}
            id="validationCustomUsername"
            placeholder="Last Name"
            value={userInfo?.lastName || ""}
            onChange={setField("lastName")}
            onBlur={touchField("lastName")}
            disabled={isLocked("lastName")}
            required
            autoComplete="off"
          />
          {fieldError("lastName")}
        </div>

        <div className="profile-field profile-field--6 profile-field--sm-half">
          <label className="form-label text--medium text--light">
            Birthdate
            {!isLocked("dob") && (
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            )}
            {lockIcon("dob")}
          </label>
          <input
            type="date"
            id="dob"
            className={inputClass("dob")}
            name="dob"
            max={getMaxDateOfBirth()}
            placeholder="01/01/2000"
            value={userInfo?.dob || ""}
            onChange={setField("dob")}
            onBlur={touchField("dob")}
            disabled={isLocked("dob")}
            required
            autoComplete="off"
          />
          {fieldError("dob")}
        </div>
        <div className="profile-field profile-field--6">
          <label
            for="country"
            className="form-label text--medium text--light"
          >
            Country
            <span aria-hidden="true" className="required-asterisk">
              {" *"}
            </span>
          </label>
          <input
            type="text"
            className={inputClass("country")}
            id="country"
            placeholder="Country"
            value={userInfo?.country || ""}
            onChange={setField("country")}
            onBlur={touchField("country")}
            required
            autoComplete="off"
          />
          {fieldError("country")}
        </div>
        <div className="profile-field profile-field--6">
          <label
            for="state"
            className="form-label text--medium text--light"
          >
            State
          </label>
          <input
            type="text"
            className={inputClass("state")}
            id="state"
            placeholder="State"
            value={userInfo?.state || ""}
            onChange={setField("state")}
            onBlur={touchField("state")}
            autoComplete="off"
          />
          {fieldError("state")}
        </div>
        <div className="profile-field profile-field--6">
          <label
            for="city"
            className="form-label text--medium text--light"
          >
            City
          </label>
          <input
            type="text"
            className={inputClass("city")}
            id="city"
            placeholder="City"
            value={userInfo?.city || ""}
            onChange={setField("city")}
            onBlur={touchField("city")}
            autoComplete="off"
          />
          {fieldError("city")}
        </div>
        <div className="profile-field profile-field--9">
          <label
            for="address"
            className="form-label text--medium text--light"
          >
            Address
          </label>
          <input
            type="text"
            className={inputClass("address")}
            id="address"
            placeholder="Address"
            value={userInfo?.address || ""}
            onChange={setField("address")}
            onBlur={touchField("address")}
            autoComplete="off"
          />
          {fieldError("address")}
        </div>
        <div className="profile-field profile-field--3">
          <label
            for="pincode"
            className="form-label text--medium text--light"
          >
            Pin Code
          </label>
          <input
            type="text"
            className={inputClass("pincode")}
            id="pincode"
            placeholder="Pin Code"
            value={userInfo?.pincode || ""}
            onChange={setField("pincode")}
            onBlur={touchField("pincode")}
            autoComplete="off"
          />
          {fieldError("pincode")}
        </div>
        {isMobile && <div className="profile-field--full">{updateButton}</div>}
      </form>
    </div>
  );
}
